import { useCallback, useEffect, useState } from 'react';

/**
 * useViewportHeight
 * Returns the available viewport height for an element based on its current
 * position within the viewport. Height is recalculated on window resize.
 * Optionally, you can opt-in to update on scroll as well by setting reactToScroll = true.
 *
 * @param getTop A function that returns the element's top position relative to viewport (in px).
 *               Typically: () => ref.current?.getBoundingClientRect().top ?? 0
 * @param extraOffset Extra pixels to subtract (padding, margins, sticky headers), default 0
 * @param minHeight Minimum height to enforce, default 360
 * @param reactToScroll If true, also recompute on scroll. Default false to avoid re-rendering while scrolling.
 */
export function useViewportHeight(
  getTop: () => number,
  extraOffset: number = 0,
  minHeight: number = 360,
  reactToScroll: boolean = false
): number {
  const compute = useCallback(() => {
    const top = Math.max(0, getTop() || 0);
    const h = window.innerHeight - top - extraOffset;
    return Math.max(minHeight, h);
  }, [getTop, extraOffset, minHeight]);

  const [height, setHeight] = useState<number>(() => {
    if (typeof window === 'undefined') return minHeight;
    return compute();
  });

  useEffect(() => {
    const onResize = () => setHeight(compute());
    // By default, do NOT recompute on scroll to prevent chart re-renders while scrolling
    const onScroll = () => {
      if (reactToScroll) setHeight(compute());
    };
    window.addEventListener('resize', onResize);
    if (reactToScroll) window.addEventListener('scroll', onScroll, { passive: true });
    // Initial compute in case refs/layout shift after first paint
    const id = window.setTimeout(onResize, 0);
    return () => {
      window.removeEventListener('resize', onResize);
      if (reactToScroll) window.removeEventListener('scroll', onScroll);
      window.clearTimeout(id);
    };
  }, [compute, reactToScroll]);

  return height;
}

export default useViewportHeight;
